import { useState, useRef, useEffect, useMemo } from 'react';
import { Search, Telescope, X, ChevronDown } from 'lucide-react';
import { palette, transition, topbar as tb, admin } from '../../styles/tokens';
import { useAuthStore } from '../../stores/authStore';
import { usePlatformStore } from '../../stores/platformStore';
import { INDUSTRY_CATEGORIES } from '../../data/industryTaxonomy';
import { useIndustryLens } from '../../hooks/useIndustryLens';

interface TopBarProps {
  onSearchClick?: () => void;
}

// Group industries by cluster for the dropdown
const CLUSTER_GROUPS = (() => {
  const groups: { label: string; items: typeof INDUSTRY_CATEGORIES }[] = [];
  const clusterMap = new Map<string, { label: string; items: typeof INDUSTRY_CATEGORIES }>();
  for (const cat of INDUSTRY_CATEGORIES) {
    const key = cat.id === 'other' ? 'Other' : (() => {
      // Derive cluster label from the category's position in the array
      const idx = INDUSTRY_CATEGORIES.indexOf(cat);
      if (idx < 4) return 'Appointment';
      if (idx < 7) return 'Field Service';
      if (idx < 10) return 'Route-Based';
      if (idx < 11) return 'Property';
      if (idx < 14) return 'Event / Project';
      if (idx < 15) return 'Automotive';
      if (idx < 16) return 'Transport';
      return 'Other';
    })();
    if (!clusterMap.has(key)) clusterMap.set(key, { label: key, items: [] });
    clusterMap.get(key)!.items.push(cat);
  }
  for (const g of clusterMap.values()) groups.push(g);
  return groups;
})();

export function TopBar({ onSearchClick }: TopBarProps) {
  const user = useAuthStore(s => s.user);
  const ownIndustry = useAuthStore(s => s.business?.industry ?? null);
  const isPlatformAdmin = user?.is_platform_admin === true;
  const { setIndustryLens, clearLens } = usePlatformStore();
  const { isLensActive, industryName, clusterLabel, features } = useIndustryLens();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    if (!dropdownOpen) return;
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [dropdownOpen]);

  // Count hidden sidebar items for the badge
  const hiddenCount = useMemo(() => {
    if (!isLensActive) return 0;
    let count = 0;
    if (!features.showJobs) count++;
    if (!features.showBookings) count++;
    if (!features.showFleet) count++;
    if (!features.showDispatch) count++;
    if (!features.showProjects) count++;
    if (!features.showLocations) count++;
    return count;
  }, [isLensActive, features]);

  const filteredGroups = useMemo(() => {
    if (!search) return CLUSTER_GROUPS;
    const q = search.toLowerCase();
    return CLUSTER_GROUPS
      .map(g => ({ ...g, items: g.items.filter(c => c.label.toLowerCase().includes(q)) }))
      .filter(g => g.items.length > 0);
  }, [search]);

  return (
    <div style={{
      height: tb.height, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 24px',
      borderBottom: `0.5px solid ${admin.border}`,
      background: isLensActive ? '#FEF3C720' : tb.bg,
      flexShrink: 0, position: 'sticky', top: 0, zIndex: 10,
    }}>
      {/* Left: Search bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, maxWidth: 400 }}>
        <div
          onClick={onSearchClick}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, flex: 1,
            height: 36, padding: '0 12px', borderRadius: 8,
            background: tb.searchBg,
            border: `0.5px solid ${tb.searchBorder}`,
            cursor: 'pointer',
            transition: `border-color ${transition.fast}`,
          }}
          onMouseEnter={e => (e.currentTarget.style.borderColor = admin.borderHover)}
          onMouseLeave={e => (e.currentTarget.style.borderColor = tb.searchBorder)}
        >
          <Search size={16} color={admin.caption} style={{ opacity: 0.5 }} />
          <span style={{ fontSize: 13, color: admin.caption, flex: 1 }}>Search anything...</span>
          <span style={{
            fontSize: 10, color: admin.caption,
            padding: '1px 5px', background: palette.white,
            border: `0.5px solid ${admin.border}`, borderRadius: 4,
          }}>&#8984;K</span>
        </div>

        {/* Lens badge */}
        {isLensActive && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '3px 10px',
            borderRadius: 12, background: '#FEF3C7', border: '1px solid #F59E0B40',
            whiteSpace: 'nowrap',
          }}>
            <Telescope size={12} color={palette.amber} />
            <span style={{ fontSize: 11, fontWeight: 600, color: '#92400E' }}>
              {industryName}{clusterLabel ? ` (${clusterLabel})` : ''}
            </span>
            {hiddenCount > 0 && (
              <span style={{ fontSize: 9, fontWeight: 600, color: '#92400E', background: '#FDE68A', padding: '0 4px', borderRadius: 6 }}>
                {hiddenCount} hidden
              </span>
            )}
            <button onClick={() => { clearLens(); setDropdownOpen(false); }} style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              display: 'flex', alignItems: 'center',
            }}>
              <X size={12} color="#92400E" />
            </button>
          </div>
        )}
      </div>

      {/* Right: Lens switcher + Notification */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {isPlatformAdmin && (
          <div ref={dropdownRef} style={{ position: 'relative' }}>
            <button
              onClick={() => { setDropdownOpen(!dropdownOpen); setSearch(''); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '5px 10px', borderRadius: 6,
                border: `1px solid ${isLensActive ? palette.amber : admin.border}`,
                fontSize: 12, fontFamily: 'inherit', color: admin.body,
                background: isLensActive ? '#FFFBEB' : palette.white,
                cursor: 'pointer',
              }}
            >
              <Telescope size={14} color={isLensActive ? palette.amber : admin.caption} />
              <span>{isLensActive ? industryName : 'Industry Lens'}</span>
              <ChevronDown size={12} color={admin.caption} />
            </button>

            {dropdownOpen && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: 4,
                width: 280, maxHeight: 400, overflowY: 'auto',
                background: palette.white, borderRadius: 10,
                border: `1px solid ${admin.border}`,
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                zIndex: 100,
              }}>
                {/* Search */}
                <div style={{ padding: '8px 10px', borderBottom: `1px solid ${palette.g100}` }}>
                  <input
                    autoFocus
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search industries..."
                    style={{
                      width: '100%', padding: '6px 10px', borderRadius: 6,
                      border: `1px solid ${palette.g200}`, fontSize: 12,
                      fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                </div>

                {/* Clear option */}
                {isLensActive && !search && (
                  <button
                    onClick={() => { clearLens(); setDropdownOpen(false); }}
                    style={{
                      width: '100%', padding: '8px 14px', border: 'none',
                      background: '#FEF3C7', fontSize: 12, fontWeight: 600,
                      color: '#92400E', cursor: 'pointer', fontFamily: 'inherit',
                      textAlign: 'left', borderBottom: `1px solid ${palette.g100}`,
                    }}
                  >
                    Exit Lens — Return to Live View
                  </button>
                )}

                {/* Grouped industries */}
                {filteredGroups.map(group => (
                  <div key={group.label}>
                    <div style={{
                      padding: '8px 14px 4px', fontSize: 10, fontWeight: 600,
                      color: palette.g400, textTransform: 'uppercase', letterSpacing: '0.06em',
                    }}>
                      {group.label}
                    </div>
                    {group.items.map(cat => {
                      const isOwn = cat.id === ownIndustry;
                      const isSelected = isLensActive && cat.id === usePlatformStore.getState().lensIndustryId;
                      return (
                        <button
                          key={cat.id}
                          onClick={() => {
                            setIndustryLens(cat.id);
                            setDropdownOpen(false);
                            setSearch('');
                          }}
                          style={{
                            width: '100%', padding: '7px 14px', border: 'none',
                            background: isSelected ? palette.g100 : 'transparent',
                            fontSize: 12.5, color: palette.g700,
                            cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            transition: `background ${transition.fast}`,
                          }}
                          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = palette.g50; }}
                          onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                        >
                          <span>{cat.label}</span>
                          {isOwn && (
                            <span style={{ fontSize: 10, color: palette.g400, fontStyle: 'italic' }}>yours</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/*
          TODO(post-launch): Admin notifications inbox

          The notification bell was previously wired to hardcoded MOCK_NOTIFS data
          and has been hidden. Building the real inbox requires:
          - New admin_notifications table (event_type, user_id, business_id, title, body, data, read_at, created_at)
          - RLS per-user scoped to business
          - Database triggers for: invoice paid, booking received, job status changes, review posted, team member joined, Stripe payout completed
          - Realtime subscription via supabase.channel()
          - useAdminNotifications hook with mark-as-read RPC
          - Restore the bell UI below and wire to the real hook

          Do NOT restore this block until the real data model is in place.
          Dedicated session, ~10-15 files, /think max.
        */}
      </div>
    </div>
  );
}

